import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Eye, MousePointer, Target, DollarSign, TrendingUp, Clock, Download, Info, ArrowUp, ArrowDown, ChevronUp, ChevronDown, Loader2, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { CPDCInfoButton } from "@/components/ui/MetricInfo";
import { MetricCard, CHART_COLORS } from "@/components/MetricCard";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { format } from "date-fns";

const COLORS = ['#3B82F6', '#F97316', '#EAB308', '#A855F7', '#22C55E'];

// Info tooltip component
const InfoTooltip = ({ content }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button className="ml-1 text-muted-foreground hover:text-foreground transition-colors">
          <Info className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="text-sm">{content}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export default function Analytics() {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("all");
  const [loading, setLoading] = useState(true);
  const [campaignPerformance, setCampaignPerformance] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'spend', direction: 'desc' });
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState("30"); // days: 7, 30, 60, 90, custom
  const [customDays, setCustomDays] = useState(30);
  const [hourlyData, setHourlyData] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedCampaign, dateRange, customDays]);

  const getDaysValue = () => {
    if (dateRange === "custom") return customDays;
    return parseInt(dateRange);
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const days = getDaysValue();
      
      if (selectedCampaign === "all") {
        const [analyticsRes, timeseriesRes, campaignsRes, hourlyRes, perfRes] = await Promise.all([
          axios.get(`${API}/analytics/summary?days=${days}`, { headers }),
          axios.get(`${API}/analytics/timeseries?days=${days}`, { headers }),
          axios.get(`${API}/campaigns`, { headers }),
          axios.get(`${API}/analytics/hourly?days=${days}`, { headers }),
          axios.get(`${API}/analytics/campaign-performance?days=${days}`, { headers })
        ]);
        setAnalytics(analyticsRes.data);
        setTimeseries(timeseriesRes.data);
        setCampaigns(campaignsRes.data);
        setHourlyData(hourlyRes.data);
        setCampaignPerformance(perfRes.data);
      } else {
        const campaignAnalytics = await axios.get(`${API}/analytics/campaigns/${selectedCampaign}`, { headers });
        setAnalytics(campaignAnalytics.data.summary);
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/analytics/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `booma_analytics_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV exported successfully", {
        description: "Your analytics data has been downloaded"
      });
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed", {
        description: "Unable to export analytics data. Please try again."
      });
    } finally {
      setExporting(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass-heavy rounded-lg p-3 border border-border shadow-xl">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs" style={{ color: entry.color }}>
              {entry.name}: {entry.name === 'spend' ? `$${entry.value.toFixed(2)}` : entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Spend breakdown by metric type - real per-metric spend from API only
  const metricBreakdown = [
    { name: 'Views', value: analytics?.spend_views || 0, color: '#3B82F6' },
    { name: 'Clicks', value: analytics?.spend_clicks || 0, color: '#F97316' },
    { name: 'Delivered Customers', value: (analytics?.spend_cpdc_direct || 0) + (analytics?.spend_cpdc_proximity || 0) + (analytics?.spend_cpdc_prompted || 0), color: '#A855F7' },
  ];

  // Sort campaigns
  const sortedCampaigns = [...campaignPerformance].sort((a, b) => {
    if (sortConfig.direction === 'asc') {
      return a[sortConfig.key] > b[sortConfig.key] ? 1 : -1;
    }
    return a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
  });

  const topCampaigns = sortedCampaigns.slice(0, 5);
  const bottomCampaigns = [...campaignPerformance].sort((a, b) => a.spend - b.spend).slice(0, 5);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Analytics</h1>
          <p className="text-muted-foreground mt-1">
            Performance metrics and insights
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Date Range Selector */}
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-36" data-testid="date-range-filter">
                <SelectValue placeholder="Date range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 6 months</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            {dateRange === "custom" && (
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="1"
                  max="730"
                  value={customDays}
                  onChange={(e) => setCustomDays(Math.min(730, Math.max(1, parseInt(e.target.value) || 30)))}
                  className="w-16 h-9 px-2 rounded-md border border-input bg-background text-sm"
                />
                <span className="text-sm text-muted-foreground">days</span>
              </div>
            )}
          </div>
          
          <Button variant="outline" onClick={handleExportCSV} disabled={exporting} data-testid="export-csv-btn">
            {exporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </>
            )}
          </Button>
          <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
            <SelectTrigger className="w-64" data-testid="campaign-filter">
              <SelectValue placeholder="Select campaign" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Campaigns</SelectItem>
              {campaigns.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {/* Views */}
        <MetricCard 
          type="impressions" 
          value={analytics?.impressions} 
          testId="analytics-impressions"
        />
        
        {/* Clicks with CTR */}
        <MetricCard 
          type="clicks" 
          value={analytics?.clicks}
          ctr={analytics?.impressions ? ((analytics.clicks / analytics.impressions) * 100).toFixed(2) : 0}
          testId="analytics-clicks"
        />
        
        {/* Deals Saved */}
        <Card className="bg-card border-border" data-testid="analytics-deals">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Deals Saved</p>
                <p className="text-2xl font-bold mt-1">{analytics?.deals_saved?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">By passengers</p>
              </div>
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <Bookmark className="h-5 w-5 text-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Delivered Customers */}
        <MetricCard 
          type="cpdc" 
          value={(analytics?.cpdc_direct || 0) + (analytics?.cpdc_proximity || 0) + (analytics?.cpdc_prompted || 0) || analytics?.cpdc || 0}
          direct={analytics?.cpdc_direct || 0}
          proximity={analytics?.cpdc_proximity || 0}
          prompted={analytics?.cpdc_prompted || 0}
          testId="analytics-cpdc"
        />
        
        {/* Total Spend */}
        <MetricCard 
          type="spend" 
          value={analytics?.spend}
          testId="analytics-spend"
        />
      </div>

      {/* Campaign Performance - Top & Bottom */}
      {campaignPerformance.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performing Campaigns */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowUp className="h-5 w-5 text-green-500" />
                Most Successful Campaigns
              </CardTitle>
              <CardDescription>Top 5 by spend performance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topCampaigns.map((campaign, index) => (
                  <div key={campaign.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-green-500/20 text-green-500 text-xs font-bold flex items-center justify-center">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm">{campaign.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {campaign.impressions.toLocaleString()} views • {campaign.clicks.toLocaleString()} clicks
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-500">${campaign.spend.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{campaign.arrivals} arrivals</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Least Performing Campaigns */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowDown className="h-5 w-5 text-amber-500" />
                Least Successful Campaigns
              </CardTitle>
              <CardDescription>Bottom 5 - may need optimization</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {bottomCampaigns.map((campaign, index) => (
                  <div key={campaign.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-500 text-xs font-bold flex items-center justify-center">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm">{campaign.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {campaign.impressions.toLocaleString()} views • {campaign.clicks.toLocaleString()} clicks
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-amber-500">${campaign.spend.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{campaign.arrivals} arrivals</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* All Campaigns Table */}
      {campaignPerformance.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>All Campaigns Performance</CardTitle>
            <CardDescription>Click column headers to sort</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th 
                      className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('name')}
                    >
                      <span className="flex items-center gap-1">Campaign Name <SortIcon columnKey="name" /></span>
                    </th>
                    <th 
                      className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('start_at')}
                    >
                      <span className="flex items-center gap-1">Start Date <SortIcon columnKey="start_at" /></span>
                    </th>
                    <th 
                      className="text-right p-3 text-xs text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('impressions')}
                    >
                      <span className="flex items-center justify-end gap-1">Views <SortIcon columnKey="impressions" /></span>
                    </th>
                    <th 
                      className="text-right p-3 text-xs text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('clicks')}
                    >
                      <span className="flex items-center justify-end gap-1">Clicks <SortIcon columnKey="clicks" /></span>
                    </th>
                    <th 
                      className="text-right p-3 text-xs text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('arrivals')}
                    >
                      <span className="flex items-center justify-end gap-1">Arrivals <SortIcon columnKey="arrivals" /></span>
                    </th>
                    <th 
                      className="text-right p-3 text-xs text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('ctr')}
                    >
                      <span className="flex items-center justify-end gap-1">CTR % <SortIcon columnKey="ctr" /></span>
                    </th>
                    <th 
                      className="text-right p-3 text-xs text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('spend')}
                    >
                      <span className="flex items-center justify-end gap-1">Spend <SortIcon columnKey="spend" /></span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCampaigns.map((campaign) => (
                    <tr key={campaign.id} className="border-b border-border/50 hover:bg-secondary/20">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{campaign.name}</span>
                          <Badge variant="outline" className={
                            campaign.status === 'ACTIVE' ? 'border-green-500/30 text-green-500' :
                            campaign.status === 'PAUSED' ? 'border-amber-500/30 text-amber-500' :
                            'border-muted-foreground/30'
                          }>
                            {campaign.status}
                          </Badge>
                        </div>
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {campaign.start_at ? format(new Date(campaign.start_at), "MMM d, yyyy") : '-'}
                      </td>
                      <td className="p-3 text-right">{campaign.impressions.toLocaleString()}</td>
                      <td className="p-3 text-right">{campaign.clicks.toLocaleString()}</td>
                      <td className="p-3 text-right">{campaign.arrivals.toLocaleString()}</td>
                      <td className="p-3 text-right">{campaign.ctr}%</td>
                      <td className="p-3 text-right font-medium">${campaign.spend.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Performance Chart */}
        <Card className="bg-card border-border lg:col-span-2" data-testid="timeseries-chart">
          <CardHeader>
            <CardTitle>Performance Trend</CardTitle>
            <CardDescription>
              {dateRange === "7" ? "Last 7 days" :
               dateRange === "30" ? "Last 30 days" :
               dateRange === "60" ? "Last 60 days" :
               dateRange === "90" ? "Last 90 days" :
               dateRange === "180" ? "Last 6 months" :
               dateRange === "365" ? "Last year" :
               dateRange === "custom" ? `Last ${customDays} days` : "Last 30 days"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeseries}>
                  <defs>
                    <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F97316" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#F97316" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorDelivered" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#A855F7" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#A855F7" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#A1A1AA"
                    fontSize={11}
                    tickFormatter={(value) => format(new Date(value), "MMM d")}
                    interval="preserveStartEnd"
                  />
                  <YAxis yAxisId="left" stroke="#3B82F6" fontSize={11} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <YAxis yAxisId="right" orientation="right" stroke="#A1A1AA" fontSize={11} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="impressions"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorImpressions)"
                    name="Views"
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="clicks"
                    stroke="#F97316"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorClicks)"
                    name="Clicks"
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="delivered_customers"
                    stroke="#A855F7"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorDelivered)"
                    name="Delivered Customers"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Spend Breakdown */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Spend Breakdown</CardTitle>
            <CardDescription>By billing metric</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {metricBreakdown.some(m => m.value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={metricBreakdown.filter(m => m.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {metricBreakdown.filter(m => m.value > 0).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0];
                        return (
                          <div className="glass-heavy rounded-lg px-3 py-2 border border-border shadow-xl">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: data.payload.color }} />
                              <span className="text-sm font-medium text-foreground">{data.payload.name}</span>
                            </div>
                            <p className="text-sm font-bold text-foreground mt-1">
                              ${data.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                  No spend data yet
                </div>
              )}
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {metricBreakdown.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-sm text-muted-foreground">
                    {item.name}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hourly Heatmap */}
      <Card className="bg-card border-border" data-testid="hourly-chart">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle>Time of Day Performance</CardTitle>
              <CardDescription>Hourly view distribution</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            {hourlyData.some(d => d.impressions > 0) ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis 
                  dataKey="hourLabel" 
                  stroke="#A1A1AA" 
                  fontSize={10}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={50}
                />
                <YAxis stroke="#A1A1AA" fontSize={11} />
                <RechartsTooltip 
                  contentStyle={{ 
                    backgroundColor: '#121214', 
                    border: '1px solid #27272A',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(label) => `Hour: ${label}`}
                />
                <Bar dataKey="impressions" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Views" />
              </BarChart>
            </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                No hourly data yet
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
